import React, { useState, useEffect } from "react";
import { ResponsiveTreeMap } from "@nivo/treemap";
import { stocks } from "../../devdata/maps/chartred";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faArrowRight,
  faChartColumn,
} from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import { Subhai } from "..";
import { useUserContext } from "../../context";
import { colors } from "../../devdata/constants/themes";
import * as d3 from "d3";

const Treemap = ({ big }) => {
  const [selectedSector, setSelectedSector] = useState(null);
  const [selectedSectordata, setSelectedSectordata] = useState(null);
  const { userdetails } = useUserContext();

  const [data, setData] = useState([]);
  const [alld, setalld] = useState([]);
  const [sectorData, setSectorData] = useState(null);
  const [updatedTime, setUpdatedTime] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const storedData = sessionStorage.getItem("index");
    const alldatastored = sessionStorage.getItem("all");
    if (alldatastored && alldatastored !== "undefined") {
      setalld(JSON.parse(alldatastored) || []);
    }
    if (storedData && storedData !== "undefined") {
      setData(JSON.parse(storedData) || []);
      setUpdatedTime(
        new Date().toLocaleString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
      );
    }
    const interval = setInterval(() => {
      const storedData = sessionStorage.getItem("index");
      const alldatastored = sessionStorage.getItem("all");
      if (alldatastored && alldatastored !== "undefined") {
        setalld(JSON.parse(alldatastored) || []);
      }
    }, 1000);
    setLoading(false);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (data.length > 0) {
      const sortedData = [...data].sort((a, b) => b.per_chg - a.per_chg); // Sort sectors by per_chg DESC
      const secData = {
        name: "All Sectors",
        children: sortedData.map(({ name, per_chg, nsecode }) => ({
          name: name,
          nsecode: name,
          value: Math.abs(per_chg) + 2, // Adjusted value for Nivo
          sectorChange: per_chg,
          stockChange: per_chg,
        })),
      };
      setSectorData(secData);
    }
  }, [data]);

  useEffect(() => {
    if (selectedSector) {
      const sectorStocks = stocks.filter(
        (stock) => stock.sector === selectedSector
      );
      const sortedStocks = [...sectorStocks].sort((a, b) => {
        const aVolumeData = alld?.find((item) => item.nsecode === a.nsecode);
        const bVolumeData = alld?.find((item) => item.nsecode === b.nsecode);
        const aChg = aVolumeData ? aVolumeData.per_chg : -999;
        const bChg = bVolumeData ? bVolumeData.per_chg : -999;
        return bChg - aChg;
      });

      const selectedStockData = {
        name: selectedSector,
        children: sortedStocks
          .map((stock) => {
            const volumeData = alld?.find(
              (item) => item.nsecode === stock.nsecode
            );
            if (volumeData) {
              return {
                name: stock.name,
                nsecode: stock.nsecode,
                value: Math.abs(volumeData.per_chg) + 2, // Adjusted value for Nivo
                stockChange: volumeData.per_chg,
                sectorChange: volumeData.per_chg,
              };
            }
            return null;
          })
          .filter(Boolean),
      };
      setSelectedSectordata(selectedStockData);
    } else {
      setSelectedSectordata(null);
    }
  }, [selectedSector, alld]);

  const minChange = d3.min(data, (d) => d.per_chg) || -10;
  const maxChange = d3.max(data, (d) => d.per_chg) || 10;

  // Adjust domain dynamically
  const positiveColorScale = d3
    .scaleLinear()
    .domain([0, maxChange]) // Adapt to actual max positive change
    .range(["#019853", "#01BE68"]) // Light Green to Dark Green
    .clamp(true);

  const negativeColorScale = d3
    .scaleLinear()
    .domain([minChange, 0]) // Adapt to actual min negative change
    .range(["#FF6259", "#FF746C"]) // Dark Red to Light Red
    .clamp(true);

  const colorScale = (node) => {
    // console.log(node.treeDepth);
    const change =
      node.treeDepth === 1 ? node.data.sectorChange : node.data.stockChange; // Determine change value

    if (node.treeDepth === 0) {
      return colors.darkMode.layer1;
    }
    // console.log(change);
    if (change > 0) {
      return positiveColorScale(change); // Use positive scale for positive changes
    } else if (change < 0) {
      return negativeColorScale(change); // Use negative scale for negative changes
    } else {
      return colors.darkMode.layer3; // Neutral color (optional, for zero change)
    }
  };

  const CustomNode = ({ node }) => {
    const fillColor = colorScale(node); // Use colorScale directly for color
    const data = node.data;
    // console.log(data);
    return (
      <g
        className="cursor-pointer"
        transform={`translate(${node.x}, ${node.y})`}
        onClick={() => node.onClick(node)}
        pointerEvents="all"
      >
        {/* Rectangle for background */}
        <rect
          width={node.width}
          height={node.height}
          fill={fillColor} // Apply the correct color
          rx={6} // Rounded corners
          ry={6}
        />

        {/* Stock/Sector Name */}
        <text
          x={node.width / 2}
          y={node.height / 3}
          textAnchor="middle"
          fill="white"
          fontSize={Math.min(node.width / 8, node.height / 3, 14)} // Responsive font size
          fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
          fontWeight="600"
        >
          {data.nsecode > 10
            ? node.width < 100
              ? data.name.slice(0, 6) + "..." // Shorter text for small boxes
              : data.name.slice(0, 10) + "..."
            : data.nsecode}
        </text>

        {/* Percentage Change Pill - fixed size */}
        {Math.abs(data.stockChange) && (
          <>
            <rect
              x={node.width / 2 - 22} // Fixed width positioning
              y={node.height / 2}
              width={44} // Fixed width
              height={18} // Fixed height
              fill={
                data.stockChange > 0
                  ? colors.darkMode.darkGreen
                  : colors.darkMode.redDarker
              }
              rx={8}
              ry={8}
            />
            <text
              x={node.width / 2}
              y={node.height / 2 + 12}
              textAnchor="middle"
              fill={
                data.stockChange > 0
                  ? colors.darkMode.green
                  : colors.darkMode.text
              }
              fontSize={Math.min(node.width / 5, 12)} // Only make font responsive up to 12px
              fontFamily="Roboto Mono, monospace"
              fontWeight="400"
            >
              {Math.abs(data.stockChange)}%
            </text>
          </>
        )}
      </g>
    );
  };

  const theme = {
    labels: {
      text: {
        fill: "#FFFFFF", // White color
        fontWeight: 600, // Semi-bold
        fontSize: 16, // Increased font size
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif", // System font stack
      },
    },
  };

  // const theme = {
  //   // Nivo Theme for Dark Mode Consistency
  //   textColor: colors.darkMode.text,
  //   fontSize: 14,
  //   fontFamily: "sans-serif",
  // };

  const commonNodeProps = {
    labelTextColor: "inherit",
  };

  const bigCommonNodeProps = {
    ...commonNodeProps,
    fontSize: 16,
  };

  const currentData = selectedSector ? selectedSectordata : sectorData;

  return (
    <div
      className="relative flex flex-col items-center bg-darkMode-layer1 px-4 py-4 rounded-xl shadow-lg"
      style={{
        minHeight:
          userdetails?.isverified === false || userdetails?.subexp === true
            ? 400
            : 0,
        maxHeight: 650,
      }}
    >
      <div
        className="flex gap-10 justify-between items-center w-full text-lg font-medium leading-loose text-darkMode-text"
        style={{ marginBottom: 8 }}
      >
        {selectedSector && (
          <div className="flex flex-row gap-3 items-center">
            <button
              onClick={() => {
                setSelectedSector(null);
                setSelectedSectordata(null); // Reset selected sector data
              }}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <div className="text-xl font-semibold text-white">
              {selectedSector}
              <span className="text-darkMode-subText">(Stocks)</span>
            </div>
          </div>
        )}

        <div>
          <h2 className="text-xl font-semibold text-white">Sector Analysis</h2>
          {big && (
            <div className="flex flex-row w-full justify-start ">
              <div className="bg-darkMode-layer2 px-2 py-1 rounded-full text-xs text-darkMode-subText mt-2 mb-4">
                {updatedTime && `Last Updated: ${updatedTime}`}
              </div>
            </div>
          )}
        </div>
        {!big && (
          <Link to={"/sector"}>
            <div style={{ rotate: "-45deg" }}>
              <FontAwesomeIcon icon={faArrowRight} />
            </div>
          </Link>
        )}
      </div>

      <div className={`relative w-full`} style={{ height: big ? 450 : 280 }}>
        {loading ||
        !currentData ||
        !currentData.children ||
        currentData.children.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-6 text-center">
            <FontAwesomeIcon
              icon={faChartColumn}
              className="text-darkMode-text"
              size="2x"
            />
            <p className="text-gray-400 text-lg mt-2 text-center">
              Loading Please Wait...
            </p>
          </div>
        ) : (
          <ResponsiveTreeMap
            data={currentData}
            enableParentLabel={false}
            identity="name"
            value="value"
            colors={colorScale}
            nodeComponent={CustomNode} // Apply custom node component
            theme={theme} // Apply theme
            tooltip={({ node }) => (
              <div
                style={{
                  backgroundColor: colors.darkMode.layer2,
                  color: colors.darkMode.text,
                  padding: "8px",
                  borderRadius: "4px",
                  border: `1px solid ${colors.darkMode.green}`,
                }}
              >
                <strong>{node.data.name}</strong>
              </div>
            )}
            nodeOpacity={1}
            valueFormat=".0f"
            tile="binary"
            borderColor="transparent"
            borderWidth={0}
            innerPadding={7}
            outerPadding={7}
            labelSkipSize={12} // Adjust based on your design
            enableLabel={false} // Labels are handled by the custom node
            nodeStyle={{
              cornerRadius: 10,
            }}
            theme={theme}
            onClick={(node) => {
              if (node.treeDepth === 1 && !selectedSector) {
                // Depth 1 is Sector, Depth 2 is Stock, Depth 0 is root ("All Sectors" or Sector Name)
                setSelectedSector(node.data.name);
                console.log(
                  "Stock Clicked:",
                  node.data.name,
                  node.treeDepth,
                  node.data
                );
              } else {
                const found = stocks.find(
                  (stock) => stock.nsecode === node.data.nsecode
                ).url;

                if (found) {
                  window.open(found, "_blank");
                } else {
                  console.error("URL not found");
                }
              }
            }}
            {...(big ? bigCommonNodeProps : commonNodeProps)}
          />
        )}
        <Subhai />
      </div>
    </div>
  );
};

export default Treemap;
